/**
 * Сообщить о поломке в номере — /maintenance («Облака» #31, часть 3).
 *
 * Гость описывает неисправность и прикладывает фото (до 3, presign → MinIO).
 * Создаётся заявка категории «maintenance»; фото уходят первым комментарием
 * с вложениями. Тон Friendly, «Вы» с заглавной, мобайл 375.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { ServiceRequestCreatedResponse } from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useServiceCatalog } from '../catalog/useCatalog.js';
import { usePhotoUpload } from '../catalog/usePhotoUpload.js';
import { useMaintenanceReport } from './useMaintenanceReport.js';

const MAINTENANCE_CATEGORY_CODE = 'maintenance';

const formSchema = z.object({
  description: z
    .string()
    .trim()
    .min(5, 'Опишите поломку — хотя бы пара слов')
    .max(2000, 'Описание — не больше 2000 символов'),
});

type FormValues = z.infer<typeof formSchema>;

function MaintenanceForm({ categoryId }: { categoryId: string }): JSX.Element {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: { description: '' },
  });

  const [urgent, setUrgent] = useState(false);
  const { photos, addFiles, removePhoto, isUploading, uploadedKeys } = usePhotoUpload(3);
  const mutation = useMaintenanceReport();
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState<ServiceRequestCreatedResponse | null>(null);

  const onSubmit = handleSubmit((values) => {
    setServerError(null);
    mutation.mutate(
      {
        categoryId,
        description: values.description,
        urgent,
        photoKeys: uploadedKeys,
      },
      {
        onSuccess: (resp) => setDone(resp),
        onError: (err) =>
          setServerError(
            err.message !== ''
              ? err.message
              : 'Не получилось отправить — попробуйте ещё раз.',
          ),
      },
    );
  });

  if (done) {
    return (
      <section data-testid="maintenance-success">
        <Toast message="Сообщили о поломке · принято в работу" />
        <Card>
          <h2 className="hsc-card__title">Спасибо!</h2>
          <p>
            Мы передали отелю информацию о поломке — мастер свяжется с Вами. Следить за статусом
            можно в разделе «Мои заявки».
          </p>
          <div className="rq-actions">
            <Button
              data-testid="open-maintenance-request"
              onClick={() => navigate(`/requests/${encodeURIComponent(done.id)}`)}
            >
              Открыть заявку
            </Button>
            <Button variant="ghost" onClick={() => navigate('/services')}>
              К услугам
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="maintenance-page">
      <button type="button" className="ta-back" onClick={() => navigate('/services')}>
        ‹ Услуги отеля
      </button>
      <h1 className="hsc-page__title">Поломка в номере</h1>
      <p className="ta-muted">
        Что-то не работает? Опишите и приложите фото — мы быстро пришлём мастера.
      </p>

      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        {serverError !== null && (
          <div className="ta-error-summary" role="alert" data-testid="maintenance-error">
            <strong>Не получилось отправить:</strong> {serverError}
          </div>
        )}

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="mt-description">
            Что сломалось?
          </label>
          {errors.description && (
            <span className="ta-tooltip" role="alert" id="mt-description-err">
              {errors.description.message}
            </span>
          )}
          <textarea
            id="mt-description"
            className="ta-textarea"
            rows={4}
            placeholder="Например: не включается кондиционер, течёт кран в ванной…"
            aria-invalid={errors.description ? true : undefined}
            aria-describedby={errors.description ? 'mt-description-err' : undefined}
            {...register('description')}
          />
        </div>

        <div className="ta-field">
          <span className="hsc-input__label" id="mt-photos-label">
            Фото поломки (опц., до 3)
          </span>
          <div className="rq-photos" role="group" aria-labelledby="mt-photos-label">
            {photos.map((p) => (
              <span key={p.id} className={`rq-photo rq-photo--${p.status}`}>
                {p.previewUrl !== '' ? (
                  <img className="rq-photo__img" src={p.previewUrl} alt={p.name} />
                ) : (
                  <span className="rq-photo__name">{p.name}</span>
                )}
                <button
                  type="button"
                  className="rq-photo__remove"
                  aria-label={`Убрать фото ${p.name}`}
                  onClick={() => removePhoto(p.id)}
                >
                  ✕
                </button>
              </span>
            ))}
            {photos.length < 3 && (
              <label className="rq-photo-add" aria-label="Добавить фото">
                +
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="rq-photo-add__input"
                  data-testid="maintenance-photo-input"
                  onChange={(e) => {
                    addFiles(e.currentTarget.files);
                    e.currentTarget.value = '';
                  }}
                />
              </label>
            )}
          </div>
          {photos.some((p) => p.status === 'error') && (
            <p className="rq-photo-error" role="alert">
              Часть фото не загрузилась — заявку можно отправить и без них.
            </p>
          )}
        </div>

        <div className="ta-field">
          <div className="rq-toggle-row">
            <span>
              <span className="rq-toggle-row__title" id="mt-priority-label">
                Срочно
              </span>
              <span className="rq-toggle-row__sub">Например, прорвало воду или нет света</span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={urgent}
              aria-labelledby="mt-priority-label"
              className={urgent ? 'rq-switch rq-switch--on' : 'rq-switch'}
              data-testid="maintenance-priority-toggle"
              onClick={() => setUrgent((v) => !v)}
            >
              <span className="rq-switch__thumb" />
            </button>
          </div>
        </div>

        <div className="rq-actions">
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={isUploading ? true : undefined}
          >
            {isUploading ? 'Грузим фото…' : 'Сообщить о поломке'}
          </Button>
        </div>
      </form>
    </section>
  );
}

export function MaintenanceReportPage(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useServiceCatalog(locale);

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="maintenance-skeleton">
        <Skeleton height={40} radius={12} />
        <div style={{ height: 12 }} />
        <Skeleton height={160} radius={14} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section>
        <h1 className="hsc-page__title">Поломка в номере</h1>
        <Card>
          <p role="alert">Сейчас не получается открыть форму. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
            <Button variant="ghost" onClick={() => navigate('/services')}>
              К услугам
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  const category = data.categories.find((c) => c.code === MAINTENANCE_CATEGORY_CODE);

  if (!category) {
    return (
      <section data-testid="maintenance-no-category">
        <h1 className="hsc-page__title">Поломка в номере</h1>
        <Card>
          <p>
            Сообщить о поломке можно через свободную заявку — выберите «Другое» в каталоге услуг.
          </p>
          <Button variant="secondary" onClick={() => navigate('/services')}>
            К услугам
          </Button>
        </Card>
      </section>
    );
  }

  return <MaintenanceForm categoryId={category.id} />;
}
